// src/hooks/useBulkDelete.ts
/**
 * Bulk-delete state for a queue, behind capability detection.
 *
 * ⚠️ **Capability-detected, because the endpoints arrive by SDK version.** All four queue services
 * carry `delete` and `deleteMany` as of **0.0.64**, which is **not on npm** — the registry's latest is
 * 0.0.61 and that is the pin, so every queue reports `supported: false` today and flips on its own the
 * moment the package publishes. Callers render the action **disabled with a reason** rather than hiding
 * it, so the pending dependency is visible instead of looking unbuilt.
 *
 * ⚠️ Gated **twice**: the endpoint must exist *and* the module's `AllowBulkDelete` setting must be on.
 * See `useModuleDeleteSettings`.
 *
 * ⚠️ Prefers `deleteMany`. Looping `delete` from the browser turns one intent into N requests with N
 * chances to half-fail, after which the admin cannot tell which rows went. From 0.0.64 every queue has
 * `deleteMany`, so the loop should never run — it is kept for the versions that do not, and still
 * reports the **true** count, never the attempted one.
 */
import { useCallback, useMemo, useState } from 'react';
import { classifyDeleteError, type DeleteFailure } from '@/utils/deleteError';

/** The delete surface every queue service carries from SDK 0.0.64. */
interface DeletableService {
  delete?: (id: number) => Promise<unknown>;
  deleteMany?: (ids: number[]) => Promise<unknown>;
}

/**
 * What a run actually did.
 *
 * ⚠️ `removed` is the server's count, not the selection size. Deleting a selection is a **partial
 * success by design**: rows that were already approved are skipped rather than failing the call, so
 * "delete 8" legitimately removes 5. Reporting the attempted number would quietly mislead.
 */
export interface BulkDeleteOutcome {
  removed: number;
  requested: number;
  /** Set when the call failed outright, i.e. nothing was removed. */
  failure?: DeleteFailure;
}

export interface UseBulkDeleteResult {
  /** True once the SDK exposes either method. Drives whether the action can run. */
  supported: boolean;
  /** Whether the checkbox column is showing. */
  selecting: boolean;
  toggleSelecting: () => void;
  selectedIDs: number[];
  isSelected: (id: number) => boolean;
  toggle: (id: number) => void;
  /** Select or clear every id on the current page. */
  toggleAll: (ids: number[]) => void;
  clear: () => void;
  confirmOpen: boolean;
  openConfirm: () => void;
  closeConfirm: () => void;
  busy: boolean;
  /** Runs the delete and resolves with what actually happened. Never rejects. */
  run: () => Promise<BulkDeleteOutcome>;
}

/**
 * @param entityLabel singular, lower case — used to word a failure, e.g. "intake".
 */
/**
 * Pulls the true removal count out of the response.
 *
 * ⚠️ `deleteMany` answers `{ data: { removed } }` — the count of rows that actually went, with approved
 * and missing ones skipped. Earlier this read the envelope as a bare number and fell back to the
 * selection size, which reported "8 deleted" for a run that removed 5.
 */
function readRemoved(res: unknown, requested: number): number {
  const raw = (res as { data?: unknown })?.data ?? res;
  if (typeof raw === 'number') return raw;
  const removed = (raw as { removed?: unknown })?.removed;
  if (typeof removed === 'number') return removed;
  // Shape not recognised: the call succeeded, so do not report zero, but do not invent a count either.
  console.warn('[useBulkDelete] unrecognised deleteMany response shape:', res);
  return requested;
}

export function useBulkDelete(
  service: unknown,
  allowedBySetting = false,
  entityLabel = 'record',
): UseBulkDeleteResult {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const svc = service as DeletableService;
  /**
   * Both gates must pass.
   *
   * ⚠️ The endpoint existing is not permission — the module's `AllowBulkDelete` setting decides whether
   * this office uses it at all. Either being false disables the action.
   */
  const hasEndpoint = useMemo(
    () => typeof svc?.deleteMany === 'function' || typeof svc?.delete === 'function',
    [svc],
  );
  const supported = hasEndpoint && allowedBySetting;

  const clear = useCallback(() => setSelected(new Set()), []);

  const toggleSelecting = useCallback(() => {
    setSelecting((was) => {
      // Leaving select mode drops the selection — a hidden selection acted on later is a trap.
      if (was) setSelected(new Set());
      return !was;
    });
  }, []);

  const toggle = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: number[]) => {
    setSelected((prev) => {
      const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      ids.forEach((id) => { if (allOn) next.delete(id); else next.add(id); });
      return next;
    });
  }, []);

  const selectedIDs = useMemo(() => Array.from(selected), [selected]);

  const run = useCallback(async (): Promise<BulkDeleteOutcome> => {
    const requested = selectedIDs.length;
    if (!supported || requested === 0) return { removed: 0, requested };

    setBusy(true);
    let outcome: BulkDeleteOutcome = { removed: 0, requested };
    try {
      if (typeof svc.deleteMany === 'function') {
        const res = await svc.deleteMany(selectedIDs);
        outcome = { removed: readRemoved(res, requested), requested };
      } else {
        // Fallback: one at a time, counting only what actually succeeded.
        let removed = 0;
        let lastError: unknown;
        for (const id of selectedIDs) {
          try {
            await svc.delete!(id);
            removed += 1;
          } catch (e) {
            lastError = e;
            console.error('[useBulkDelete] failed for id', id, e);
          }
        }
        outcome = {
          removed,
          requested,
          // Only a failure when nothing at all went; a partial run reports its true count instead.
          failure: removed === 0 && lastError !== undefined
            ? classifyDeleteError(lastError, entityLabel)
            : undefined,
        };
      }
    } catch (e) {
      console.error('[useBulkDelete] delete failed:', e);
      outcome = { removed: 0, requested, failure: classifyDeleteError(e, entityLabel) };
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      // ⚠️ The selection survives a failure. A 403 or a network drop removed nothing, and making the
      // admin re-tick eight rows to retry is how people give up and delete them one at a time instead.
      if (!outcome.failure) {
        setSelected(new Set());
        setSelecting(false);
      }
    }
    return outcome;
  }, [supported, selectedIDs, svc, entityLabel]);

  return {
    supported,
    selecting,
    toggleSelecting,
    selectedIDs,
    isSelected: useCallback((id: number) => selected.has(id), [selected]),
    toggle,
    toggleAll,
    clear,
    confirmOpen,
    openConfirm: useCallback(() => setConfirmOpen(true), []),
    closeConfirm: useCallback(() => setConfirmOpen(false), []),
    busy,
    run,
  };
}
